<template>
  <div
    :class="[
      'template-field',
      { open: selected, 'moving-source': isMovingSource, 'move-target': moveMode && canMoveHere }
    ]"
    @click="onFieldClick()"
  >
    <div v-if="data">
      <div class="d-flex align-items-center wrapper">
        <div class="handle">
          <feather-icon size="16" icon="MenuIcon" />
        </div>

        <div class="flex-fill field-name position-relative">
          <input
            :class="['form-control', { 'border-danger': data.errors.hasErrors }]"
            type="text"
            :placeholder="$t('maintenance.field_name')"
            v-model="data.name"
          />
          <div class="error-messages" v-if="data.errors.missingName || data.errors.missingType">
            <span v-if="data.errors.missingName" class="text-danger">
              {{ $t("maintenance.form_validations.name") }}
            </span>
            <span
              v-if="data.errors.missingName && data.errors.missingType"
              class="mx-1 text-danger"
            >
              |
            </span>
            <span v-if="data.errors.missingType" class="text-danger">
              {{ $t("maintenance.form_validations.type") }}
            </span>
          </div>
        </div>

        <div class="d-flex field-config align-items-center">
          <div class="flex-fill field-config-options">
            {{ $t(fieldName) }}
          </div>

          <button
            type="button"
            class="btn btn-sm btn-outline-primary btn-move"
            v-if="!moveMode"
            @click.stop="$emit('start-move', data)"
          >
            {{ $t("maintenance.move") }}
          </button>

          <button
            type="button"
            class="btn btn-sm btn-primary btn-move"
            v-if="isMovingSource"
            @click.stop="$emit('cancel-move')"
          >
            {{ $t("maintenance.cancel") }}
          </button>

          <span class="show-options-icon" @click.stop="showModal">
            <feather-icon size="16" icon="SettingsIcon" />
          </span>

          <div
            class="btn-remove"
            v-if="selected && !fieldData.isDefault"
            @click.stop="$emit('remove')"
          >
            <feather-icon size="16" icon="TrashIcon" />
          </div>
        </div>
      </div>

      <div v-if="moveMode && canMoveHere" class="move-drop-actions">
        <button
          type="button"
          class="btn btn-sm btn-outline-success mr-1"
          @click.stop="$emit('move-before', data)"
        >
          {{ $t("maintenance.move_above") }}
        </button>

        <button
          type="button"
          class="btn btn-sm btn-outline-success"
          @click.stop="$emit('move-after', data)"
        >
          {{ $t("maintenance.move_below") }}
        </button>
      </div>

      <div class="field-options">
        <div class="d-flex gap-3">
          <div class="form-group form-check">
            <input
              type="checkbox"
              class="form-check-input"
              :id="`check-req-${fieldData.item_id}`"
              v-model="data.options.required"
            />
            <label class="form-check-label" :for="`check-req-${fieldData.item_id}`">
              {{ $t("maintenance.required") }}
            </label>
          </div>

          <div class="form-group form-check">
            <input
              type="checkbox"
              class="form-check-input"
              :id="`check-app-${fieldData.item_id}`"
              v-model="data.options.app"
            />
            <label class="form-check-label" :for="`check-app-${fieldData.item_id}`">
              {{ $t("maintenance.show_in_app") }}
            </label>
          </div>

          <div class="form-group form-check">
            <input
              type="checkbox"
              class="form-check-input"
              :id="`check-export-${fieldData.item_id}`"
              v-model="data.options.export"
            />
            <label class="form-check-label" :for="`check-export-${fieldData.item_id}`">
              {{ $t("maintenance.export") }}
            </label>
          </div>
        </div>

        <div class="form-group form-check">
          <input
            type="checkbox"
            class="form-check-input"
            :id="`check-ocr-${fieldData.item_id}`"
            v-model="data.options.ocr"
          />
          <label class="form-check-label" :for="`check-ocr-${fieldData.item_id}`">
            OCR
          </label>
        </div>

        <!-- VISIBILIDAD -->
        <div class="mt-2 p-2 bg-white rounded border">
          <div class="d-flex align-items-center justify-content-between">
            <strong>{{ $t("maintenance.visibility") }}</strong>

            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`vis-toggle-${data.item_id}`"
                v-model="visibilityEnabled"
              />
              <label class="form-check-label small" :for="`vis-toggle-${data.item_id}`">
                {{ $t("maintenance.conditional") }}
              </label>
            </div>
          </div>

          <template v-if="visibilityEnabled">
            <div class="d-flex align-items-center mt-2 flex-wrap">
              <div class="ref-select mr-2 mb-2">
                <v-select
                  :value="singleCond.field || null"
                  :options="availableSelectRefOptions"
                  :reduce="(opt) => opt.value"
                  label="label"
                  class="style-chooser"
                  :placeholder="$t('maintenance.previous_question')"
                  :clearable="true"
                  :searchable="true"
                  @input="onVisibilityRefInput"
                  @click.stop
                />
              </div>

              <div class="flex-fill mb-2" v-if="needsValue && refOptions.length">
                <select class="form-control form-control-sm" v-model="singleValue" @click.stop>
                  <option disabled value="">{{ $t("maintenance.select_placeholder") }}</option>
                  <option v-for="opt in refOptions" :key="String(opt.value)" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>

              <button
                type="button"
                class="btn btn-link text-danger btn-sm ml-2 mb-2"
                @click.stop="clearVisibility"
              >
                {{ $t("maintenance.clear") }}
              </button>
            </div>
          </template>

          <template v-else>
            <div class="text-muted small mt-1">
              {{ $t("maintenance.always_visible") }}
            </div>
          </template>
        </div>

        <!-- AUTO-RELLENO -->
        <div class="mt-2 p-2 bg-white rounded border">
          <div class="d-flex align-items-center justify-content-between">
            <strong>{{ $t("maintenance.auto_fill") }}</strong>

            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`refill-toggle-${data.item_id}`"
                v-model="refillEnabled"
              />
              <label class="form-check-label small" :for="`refill-toggle-${data.item_id}`">
                {{ $t("maintenance.enable") }}
              </label>
            </div>
          </div>

          <template v-if="refillEnabled">
            <div class="d-flex align-items-center mt-2 flex-wrap">
              <div class="ref-select mr-2 mb-2">
                <v-select
                  :value="singleRefillCond.field || null"
                  :options="availableRefOptions"
                  :reduce="(opt) => opt.value"
                  label="label"
                  class="style-chooser"
                  :placeholder="$t('maintenance.previous_question')"
                  :clearable="true"
                  :searchable="true"
                  @input="onRefillRefInput"
                  @click.stop
                />
              </div>

              <select
                class="form-control form-control-sm mr-2 mb-2"
                v-model="refillOp"
                style="min-width: 150px"
                @change="onRefillOpChange"
                @click.stop
              >
                <option v-for="op in refillOps" :key="op.value" :value="op.value">
                  {{ $t(op.label) }}
                </option>
              </select>

              <div class="flex-fill mb-2" v-if="refillNeedsValue">
                <template v-if="refillRefOptions.length">
                  <select
                    class="form-control form-control-sm"
                    v-model="refillSingleValue"
                    @click.stop
                  >
                    <option disabled value="">{{ $t("maintenance.select_placeholder") }}</option>
                    <option v-for="opt in refillRefOptions" :key="String(opt.value)" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </template>

                <template v-else>
                  <input
                    class="form-control form-control-sm"
                    :type="refillValueInputType"
                    v-model="refillSingleValue"
                    :placeholder="refillValuePlaceholder"
                    @click.stop
                  />
                </template>
              </div>

              <button
                type="button"
                class="btn btn-link text-danger btn-sm ml-2 mb-2"
                @click.stop="clearRefill"
              >
                {{ $t("maintenance.clear") }}
              </button>
            </div>

            <div class="mt-2 p-2 bg-light rounded border">
              <div class="d-flex flex-wrap align-items-center mb-2">
                <div class="mr-4 mb-2">
                  <strong class="small d-block mb-1">{{ $t("maintenance.action_to_apply") }}</strong>

                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="radio"
                      :id="`refill-mode-same-${data.item_id}`"
                      :name="`refill-mode-${data.item_id}`"
                      value="same_value"
                      v-model="refillActionMode"
                      @click.stop
                    />
                    <label class="form-check-label small" :for="`refill-mode-same-${data.item_id}`">
                      {{ $t("maintenance.copy_reference_value") }}
                    </label>
                  </div>

                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="radio"
                      :id="`refill-mode-new-${data.item_id}`"
                      :name="`refill-mode-${data.item_id}`"
                      value="new_value"
                      v-model="refillActionMode"
                      @click.stop
                    />
                    <label class="form-check-label small" :for="`refill-mode-new-${data.item_id}`">
                      {{ $t("maintenance.apply_new_value") }}
                    </label>
                  </div>

                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="radio"
                      :id="`refill-mode-copy-other-${data.item_id}`"
                      :name="`refill-mode-${data.item_id}`"
                      value="copy_other"
                      v-model="refillActionMode"
                      @click.stop
                    />
                    <label
                      class="form-check-label small"
                      :for="`refill-mode-copy-other-${data.item_id}`"
                    >
                      {{ $t("maintenance.copy_other_value") }}
                    </label>
                  </div>
                </div>

                <div class="form-check mr-3 mb-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="`refill-ovr-${data.item_id}`"
                    v-model="refillOverwrite"
                    @click.stop
                  />
                  <label class="form-check-label small" :for="`refill-ovr-${data.item_id}`">
                    {{ $t("maintenance.overwrite_on_save") }}
                  </label>
                </div>
              </div>

              <div v-if="refillActionMode === 'new_value'" class="mt-2">
                <label class="small mb-1">{{ $t("maintenance.value_to_apply") }}</label>

                <select
                  v-if="targetOptions.length"
                  class="form-control form-control-sm"
                  v-model="refillNewValue"
                  @click.stop
                >
                  <option disabled value="">{{ $t("maintenance.select_placeholder") }}</option>
                  <option v-for="opt in targetOptions" :key="String(opt.value)" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>

                <input
                  v-else
                  class="form-control form-control-sm"
                  :type="targetValueInputType"
                  v-model="refillNewValue"
                  :placeholder="$t('maintenance.enter_value')"
                  @click.stop
                />
              </div>

              <div v-if="refillActionMode === 'copy_other'" class="mt-2">
                <label class="small mb-1 d-block">{{ $t("maintenance.field_to_copy") }}</label>

                <div class="ref-select">
                  <v-select
                    :value="refillCopyFromField || null"
                    :options="copyOtherRefOptions"
                    :reduce="(opt) => opt.value"
                    label="label"
                    class="style-chooser"
                    :placeholder="$t('maintenance.select_other_field')"
                    :clearable="true"
                    :searchable="true"
                    @input="onCopyOtherFieldInput"
                    @click.stop
                  />
                </div>
              </div>

              <div class="text-muted small mt-2">
                {{ $t("maintenance.auto_fill_note") }}
              </div>
            </div>
          </template>

          <template v-else>
            <div class="text-muted small mt-1">
              {{ $t("maintenance.no_auto_fill") }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { fieldStructure, fieldTypes } from "./config";

export default {
  components: {
    vSelect,
  },

  props: {
    data: Object,
    selected: { type: Boolean, default: false },
    sectionFields: { type: Array, default: () => [] },
    indexInSection: { type: Number, default: 0 },
    moveMode: { type: Boolean, default: false },
    isMovingSource: { type: Boolean, default: false },
    canMoveHere: { type: Boolean, default: false },
  },

  mounted() {
    this.fieldData = this.data;

    if (!this.data.options) this.$set(this.data, "options", {});
    if (!this.data.options.fieldTypeCfg) {
      this.$set(this.data.options, "fieldTypeCfg", { data: [] });
    }
    if (typeof this.data.options.export === "undefined") {
      this.$set(this.data.options, "export", false);
    }

    if (!Array.isArray(this.data.show_if)) this.$set(this.data, "show_if", []);
    if (!this.data.show_logic) this.$set(this.data, "show_logic", "all");

    if (!Array.isArray(this.data.refill_if)) this.$set(this.data, "refill_if", []);
    if (!this.data.refill_logic) this.$set(this.data, "refill_logic", "all");
  },

  data() {
    return {
      fieldData: JSON.parse(JSON.stringify(fieldStructure)),
      isOpen: false,
      refillOps: [
        { label: "==", value: "==" },
        { label: "!=", value: "!=" },
        { label: ">", value: ">" },
        { label: ">=", value: ">=" },
        { label: "<", value: "<" },
        { label: "<=", value: "<=" },
        { label: "maintenance.contains", value: "contains" },
        { label: "maintenance.empty", value: "empty" },
        { label: "maintenance.not_empty", value: "not_empty" },
      ],
    };
  },

  computed: {
    fieldName() {
      const data = fieldTypes.find((fType) => fType.type === this.data.type);
      const baseName = data ? data.name : "";

      if (this.data.type === "number" && this.data.options && this.data.options.fieldTypeCfg) {
        const numberData = this.data.options.fieldTypeCfg.data || {};
        const minValue = numberData.min;
        const maxValue = numberData.max;

        if (
          minValue !== null &&
          minValue !== undefined &&
          minValue !== "" &&
          maxValue !== null &&
          maxValue !== undefined &&
          maxValue !== ""
        ) {
          return `${this.$t(baseName)} (${minValue}, ${maxValue})`;
        }
      }

      return baseName;
    },

    visibilityEnabled: {
      get() {
        return Array.isArray(this.data.show_if) && this.data.show_if.length > 0;
      },
      set(v) {
        if (v) {
          if (!Array.isArray(this.data.show_if) || this.data.show_if.length === 0) {
            this.$set(this.data, "show_if", [{ field: "", op: "==", value: "" }]);
          }
          if (!this.data.show_logic) this.$set(this.data, "show_logic", "all");
        } else {
          this.$set(this.data, "show_if", []);
        }
      },
    },

    singleCond: {
      get() {
        if (!Array.isArray(this.data.show_if) || this.data.show_if.length === 0) {
          return { field: "", op: "==", value: "" };
        }
        const cond = this.data.show_if[0];
        if (cond.op !== "==") cond.op = "==";
        return cond;
      },
      set(c) {
        const cond = { ...c, op: "==" };
        this.$set(this.data, "show_if", [cond]);
      },
    },

    availableRefs() {
      const fields = this.sectionFields || [];
      const idx = Math.min(Math.max(this.indexInSection, 0), fields.length);
      return fields.slice(0, idx);
    },

    availableSelectRefs() {
      return this.availableRefs.filter((f) => {
        if (f.item_id === this.data.item_id) return false;
        const type = (f.type || "").toLowerCase();
        return type === "select" || type === "multiple_select";
      });
    },

    availableSelectRefOptions() {
      return this.availableSelectRefs.map((f) => ({
        label: this.buildFieldLabel(f),
        value: f.item_id,
      }));
    },

    availableRefOptions() {
      return this.availableRefs
        .filter((f) => f.item_id !== this.data.item_id)
        .map((f) => ({
          label: this.buildFieldLabel(f),
          value: f.item_id,
        }));
    },

    copyOtherRefOptions() {
      return this.availableRefs
        .filter((f) => f.item_id !== this.data.item_id)
        .map((f) => ({
          label: this.buildFieldLabel(f),
          value: f.item_id,
        }));
    },

    refField() {
      return (this.availableSelectRefs || []).find((f) => f.item_id === this.singleCond.field);
    },

    refOptions() {
      const rf = this.refField;
      if (!rf || !rf.options || !rf.options.fieldTypeCfg) return [];
      const data = rf.options.fieldTypeCfg.data || [];
      if (!Array.isArray(data)) return [];

      return data.map((d, idx) => ({
        label: d.name ?? String(d.value ?? idx),
        value: typeof d.value !== "undefined" ? d.value : d.name ?? String(idx),
      }));
    },

    singleValue: {
      get() {
        return this.singleCond.value;
      },
      set(v) {
        const c = { ...this.singleCond, op: "==", value: v };
        this.singleCond = c;
      },
    },

    needsValue() {
      return !!this.refField;
    },

    refillEnabled: {
      get() {
        return Array.isArray(this.data.refill_if) && this.data.refill_if.length > 0;
      },
      set(v) {
        if (v) {
          if (!Array.isArray(this.data.refill_if) || this.data.refill_if.length === 0) {
            this.$set(this.data, "refill_if", [
              {
                field: "",
                op: "==",
                value: "",
                same_value: true,
                new_value: "",
                copy_from_field: "",
                overwrite: true,
              },
            ]);
          }
          if (!this.data.refill_logic) this.$set(this.data, "refill_logic", "all");
        } else {
          this.$set(this.data, "refill_if", []);
        }
      },
    },

    singleRefillCond: {
      get() {
        if (!Array.isArray(this.data.refill_if) || this.data.refill_if.length === 0) {
          return {
            field: "",
            op: "==",
            value: "",
            same_value: true,
            new_value: "",
            copy_from_field: "",
            overwrite: true,
          };
        }

        const cond = this.data.refill_if[0] || {};

        if (!cond.op) cond.op = "==";
        if (typeof cond.same_value === "undefined") cond.same_value = true;
        if (typeof cond.new_value === "undefined") cond.new_value = "";
        if (typeof cond.copy_from_field === "undefined") cond.copy_from_field = "";
        if (typeof cond.overwrite === "undefined") cond.overwrite = true;

        return cond;
      },
      set(c) {
        const cond = {
          field: c.field ?? "",
          op: c.op ?? "==",
          value: typeof c.value === "undefined" ? "" : c.value,
          same_value: typeof c.same_value === "undefined" ? true : !!c.same_value,
          new_value: typeof c.new_value === "undefined" ? "" : c.new_value,
          copy_from_field:
            typeof c.copy_from_field === "undefined" ? "" : c.copy_from_field,
          overwrite: typeof c.overwrite === "undefined" ? true : !!c.overwrite,
        };

        this.$set(this.data, "refill_if", [cond]);
      },
    },

    refillRefField() {
      return (this.availableRefs || []).find((f) => f.item_id === this.singleRefillCond.field);
    },

    refillRefOptions() {
      const rf = this.refillRefField;

      if (!rf || !rf.options || !rf.options.fieldTypeCfg) return [];
      const data = rf.options.fieldTypeCfg.data || [];
      if (!Array.isArray(data)) return [];

      return data.map((d, idx) => ({
        label: d.name ?? String(d.value ?? idx),
        value: typeof d.value !== "undefined" ? d.value : d.name ?? String(idx),
      }));
    },

    refillOp: {
      get() {
        return this.singleRefillCond.op || "==";
      },
      set(v) {
        const c = { ...this.singleRefillCond, op: v };
        this.singleRefillCond = c;
      },
    },

    refillNeedsValue() {
      const op = this.singleRefillCond.op || "==";
      if (op === "empty" || op === "not_empty") return false;
      return !!this.singleRefillCond.field;
    },

    refillSingleValue: {
      get() {
        return this.singleRefillCond.value;
      },
      set(v) {
        const c = { ...this.singleRefillCond, value: v };
        this.singleRefillCond = c;
      },
    },

    refillActionMode: {
      get() {
        if (this.singleRefillCond.copy_from_field) return "copy_other";
        if (this.singleRefillCond.same_value) return "same_value";
        return "new_value";
      },
      set(mode) {
        const c = { ...this.singleRefillCond };

        if (mode === "same_value") {
          c.same_value = true;
          c.copy_from_field = "";
        } else if (mode === "copy_other") {
          c.same_value = false;
          c.copy_from_field = c.copy_from_field || "";
        } else {
          c.same_value = false;
          c.copy_from_field = "";
        }

        this.singleRefillCond = c;
      },
    },

    refillCopyFromField: {
      get() {
        return this.singleRefillCond.copy_from_field || "";
      },
      set(v) {
        const c = {
          ...this.singleRefillCond,
          same_value: false,
          copy_from_field: v || "",
        };
        this.singleRefillCond = c;
      },
    },

    refillOverwrite: {
      get() {
        return typeof this.singleRefillCond.overwrite === "undefined"
          ? true
          : !!this.singleRefillCond.overwrite;
      },
      set(v) {
        const c = { ...this.singleRefillCond, overwrite: !!v };
        this.singleRefillCond = c;
      },
    },

    refillNewValue: {
      get() {
        return this.singleRefillCond.new_value;
      },
      set(v) {
        const c = { ...this.singleRefillCond, new_value: v };
        this.singleRefillCond = c;
      },
    },

    refillValueInputType() {
      return "text";
    },

    refillValuePlaceholder() {
      const op = this.singleRefillCond.op || "==";
      if (op === "contains") return this.$t("maintenance.search_text");
      return this.$t("maintenance.value_placeholder");
    },

    targetOptions() {
      const type = (this.data.type || "").toLowerCase();
      if (type !== "select" && type !== "multiple_select") return [];

      const cfg = this.data.options?.fieldTypeCfg?.data || [];
      if (!Array.isArray(cfg)) return [];

      return cfg.map((d, idx) => ({
        label: d.name ?? String(d.value ?? idx),
        value: typeof d.value !== "undefined" ? d.value : d.name ?? String(idx),
      }));
    },

    targetValueInputType() {
      const type = (this.data.type || "").toLowerCase();
      if (type === "number") return "number";
      return "text";
    },
  },

  methods: {
    buildFieldLabel(field) {
      const typeMeta = fieldTypes.find((fType) => fType.type === field.type);
      const typeLabel = typeMeta ? this.$t(typeMeta.name) : field.type || this.$t("maintenance.field");
      const name = field.name && field.name.trim()
        ? field.name.trim()
        : `(${String(field.item_id).slice(0, 6)})`;

      return `${name} · ${typeLabel}`;
    },

    onFieldClick() {
      this.$emit("item-selected", this.fieldData);
    },

    showModal() {
      this.$emit("show-modal", this.fieldData);
    },

    onVisibilityRefInput(value) {
      if (!value) {
        this.clearVisibility();
        return;
      }

      this.singleCond = {
        ...this.singleCond,
        field: value,
        op: "==",
        value: "",
      };

      this.$nextTick(() => {
        if (!this.refField) {
          this.clearVisibility();
          return;
        }

        if (this.refOptions.length > 0) {
          this.singleValue = this.refOptions[0].value;
        } else {
          this.clearVisibility();
        }
      });
    },

    clearVisibility() {
      this.$set(this.data, "show_if", []);
      this.$set(this.data, "show_logic", "all");
    },

    onRefillRefInput(value) {
      if (!value) {
        this.clearRefill();
        return;
      }

      this.singleRefillCond = {
        ...this.singleRefillCond,
        field: value,
        value: "",
      };

      this.$nextTick(() => {
        if (!this.refillRefField) {
          this.clearRefill();
          return;
        }

        if (this.refillRefOptions.length > 0) {
          this.refillSingleValue = this.refillRefOptions[0].value;
        } else {
          this.refillSingleValue = "";
        }
      });
    },

    onRefillOpChange() {
      const op = this.refillOp;
      if (op === "empty" || op === "not_empty") {
        this.refillSingleValue = "";
      }
    },

    onCopyOtherFieldInput(value) {
      this.refillCopyFromField = value || "";
    },

    clearRefill() {
      this.$set(this.data, "refill_if", []);
      this.$set(this.data, "refill_logic", "all");
    },
  },
};
</script>

<style lang="scss" scoped>
.template-field {
  padding: 0 8px;
  height: 50px;
  overflow: hidden;
  background-color: #f4f4f4;
  border-bottom: 1px solid #d1d1d1;
  position: relative;

  &:last-child {
    border: none;
  }

  &.open {
    height: auto;
    min-height: 80px;
    overflow: visible;
    z-index: 5;
  }

  &.moving-source {
    border-left: 4px solid #007bff;
    background-color: #eef6ff;
  }

  &.move-target {
    background-color: #fafcff;
  }
}

.handle {
  cursor: move;
  margin-right: 5px;
}

.field-name {
  padding: 5px 10px;
  border-right: 1px solid #d1d1d1;
  position: relative;

  .error-messages {
    position: absolute;
    bottom: -22px;
    left: 10px;
    font-size: 0.75rem;
    white-space: nowrap;
    z-index: 10;
    background-color: #f4f4f4;
    padding: 2px 4px;
  }
}

.field-config {
  margin-left: 10px;
  width: 420px;
  gap: 8px;

  .show-options-icon {
    cursor: pointer;
  }

  .btn-remove {
    cursor: pointer;
  }
}

.btn-move {
  white-space: nowrap;
}

.move-drop-actions {
  padding: 8px 10px 0;
  background: #f8fbff;
}

.field-options {
  margin-top: 28px;
  margin-left: -10px;
  margin-right: -10px;
  background-color: #e5e5e5;
  padding: 5px 10px 10px;
  position: relative;
  z-index: 1;
  overflow: visible;

  .d-flex {
    gap: 1rem;
  }
}

.ref-select {
  min-width: 280px;
  flex: 1 1 280px;
}

::v-deep .style-chooser .vs__dropdown-toggle {
  min-height: 31px;
  border-color: #ced4da;
}

::v-deep .style-chooser .vs__selected-options {
  flex-wrap: nowrap;
}

::v-deep .style-chooser .vs__search {
  margin: 0;
}

::v-deep .vs__dropdown-menu {
  z-index: 9999;
}
</style>
